import { openDB, type IDBPDatabase } from "idb";
import {
  MIC_FLAG_LATE,
  MIC_FLAG_PLAYBACK,
  decodeVoiceFrame,
  encodeMicFrame,
  type ClientMessage,
  type DeviceRole,
  type ServerMessage,
} from "@gk/shared";

export type ConnStatus = "connecting" | "open" | "reconnecting" | "closed";

interface StoredFrame {
  k: string;
  seq: number;
  captureMs: number;
  pcm: ArrayBuffer;
}

/**
 * Every microphone frame is kept on the phone until the server confirms it is
 * stored, so a dropped signal on a farm never loses the recording. On
 * reconnect, unconfirmed frames are re-sent (marked late, recording only).
 */
class FrameStore {
  private db: Promise<IDBPDatabase>;
  constructor(private readonly prefix: string) {
    this.db = openDB("gk-frames", 1, {
      upgrade(db) {
        db.createObjectStore("frames", { keyPath: "k" });
      },
    });
  }
  private key(seq: number) {
    return `${this.prefix}|${String(seq).padStart(10, "0")}`;
  }
  async put(seq: number, captureMs: number, pcm: Int16Array) {
    const buf = pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength) as ArrayBuffer;
    await (await this.db).put("frames", { k: this.key(seq), seq, captureMs, pcm: buf } satisfies StoredFrame);
  }
  async ackUpTo(seq: number) {
    const db = await this.db;
    await db.delete("frames", IDBKeyRange.bound(this.key(0), this.key(seq)));
  }
  async after(seq: number): Promise<StoredFrame[]> {
    const db = await this.db;
    return (await db.getAll("frames", IDBKeyRange.bound(this.key(seq + 1), `${this.prefix}|~`))) as StoredFrame[];
  }
}

/** Plays streamed PCM voice gaplessly and reports when each utterance starts. */
class VoicePlayer {
  private nextAt = 0;
  private rates = new Map<string, number>();
  private started = new Set<string>();
  playingUntil = 0;
  constructor(
    private readonly ctx: AudioContext,
    private readonly onStart: (turnId: string) => void,
  ) {}
  begin(turnId: string, sampleRate: number) {
    this.rates.set(turnId, sampleRate);
  }
  chunk(turnId: string, pcm: Int16Array) {
    const rate = this.rates.get(turnId) ?? 24000;
    if (pcm.length === 0) return;
    const buf = this.ctx.createBuffer(1, pcm.length, rate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i]! / 32768;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctx.destination);
    const at = Math.max(this.ctx.currentTime + 0.04, this.nextAt);
    src.start(at);
    this.nextAt = at + buf.duration;
    this.playingUntil = this.nextAt;
    if (!this.started.has(turnId)) {
      this.started.add(turnId);
      const delay = Math.max(0, (at - this.ctx.currentTime) * 1000);
      setTimeout(() => this.onStart(turnId), delay);
    }
  }
  isPlaying(): boolean {
    return this.ctx.currentTime < this.playingUntil + 0.3;
  }
  stop() {
    this.nextAt = 0;
    this.playingUntil = 0;
  }
}

export interface LiveClientOptions {
  role: DeviceRole;
  interviewKey: string;
  hello: Omit<Extract<ClientMessage, { t: "hello" }>, "t" | "deviceId">;
  onMessage: (m: ServerMessage) => void;
  onStatus: (s: ConnStatus) => void;
  onLevel?: (rms: number) => void;
}

export function deviceId(): string {
  const k = "gk.deviceId";
  try {
    let id = localStorage.getItem(k);
    if (!id) {
      id = crypto.randomUUID().replace(/-/g, "");
      localStorage.setItem(k, id);
    }
    return id;
  } catch {
    return crypto.randomUUID().replace(/-/g, "");
  }
}

/**
 * One phone's live link: WebSocket with automatic reconnect, clock sync,
 * microphone capture and upload, and translated-voice playback.
 */
export class LiveClient {
  private ws: WebSocket | null = null;
  private status: ConnStatus = "connecting";
  private closed = false;
  private retry = 0;
  private offset = 0; // server clock - local clock (ms)
  private clockOrigin = 0;
  private live = false;
  private seq = 0;
  private store: FrameStore;
  private ctx: AudioContext | null = null;
  private mic: { stream: MediaStream; node: AudioWorkletNode } | null = null;
  private player: VoicePlayer | null = null;
  private pingTimer: number | null = null;
  private wakeLock: { release(): Promise<void> } | null = null;
  private resending = false;
  speakingFlag = false;

  constructor(private readonly o: LiveClientOptions) {
    this.store = new FrameStore(`${o.interviewKey}|${o.role}`);
    try {
      this.seq = Number(localStorage.getItem(this.seqKey()) ?? 0);
    } catch {
      this.seq = 0;
    }
    this.connect();
  }

  private seqKey() {
    return `gk.seq.${this.o.interviewKey}.${this.o.role}`;
  }

  private setStatus(s: ConnStatus) {
    this.status = s;
    this.o.onStatus(s);
  }

  private connect() {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/ws/live`);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    this.setStatus(this.retry ? "reconnecting" : "connecting");
    ws.onopen = () => {
      this.retry = 0;
      ws.send(JSON.stringify({ t: "hello", ...this.o.hello, deviceId: deviceId(), client: { ua: navigator.userAgent.slice(0, 120), tz: Intl.DateTimeFormat().resolvedOptions().timeZone } }));
      this.setStatus("open");
      this.ping();
      this.pingTimer = window.setInterval(() => this.ping(), 5000);
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data !== "string") {
        const v = decodeVoiceFrame(ev.data as ArrayBuffer);
        if (v) this.player?.chunk(v.turnId, v.pcm);
        return;
      }
      const m = JSON.parse(ev.data) as ServerMessage;
      this.handle(m);
      this.o.onMessage(m);
    };
    ws.onclose = (ev) => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.ws = null;
      // Permanent refusals: do not retry.
      if (this.closed || [4401, 4403, 4404, 4409, 4410].includes(ev.code)) {
        this.setStatus("closed");
        return;
      }
      this.retry++;
      this.setStatus("reconnecting");
      setTimeout(() => this.connect(), Math.min(8000, 400 * 2 ** Math.min(this.retry, 5)));
    };
  }

  private handle(m: ServerMessage) {
    switch (m.t) {
      case "welcome":
        this.clockOrigin = m.session.clockOrigin;
        this.live = m.session.state === "live";
        this.seq = Math.max(this.seq, m.lastAckSeq);
        void this.store.ackUpTo(m.lastAckSeq).then(() => this.resendUnacked(m.lastAckSeq));
        break;
      case "state":
        if (m.clockOrigin) this.clockOrigin = m.clockOrigin;
        this.live = m.state === "live";
        if (m.state === "ended") void this.releaseWake();
        if (m.state === "paused" || m.state === "ended") this.player?.stop();
        break;
      case "ack":
        void this.store.ackUpTo(m.seq);
        break;
      case "voice-start":
        this.player?.begin(m.turnId, m.sampleRate);
        break;
      case "pong": {
        const now = Date.now();
        this.offset = m.serverMs - (m.atMs + now) / 2;
        break;
      }
    }
  }

  private ping() {
    this.send({ t: "ping", atMs: Date.now() });
  }

  /** Session clock: ms since the interview started, on the server's clock. */
  now(): number {
    return Date.now() + this.offset - this.clockOrigin;
  }

  send(m: ClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  get connectionStatus() {
    return this.status;
  }

  /** Must be called from a tap: unlocks audio on iOS and asks for the microphone. */
  async enableAudio(): Promise<void> {
    if (!this.ctx) {
      this.ctx = new AudioContext({ latencyHint: "interactive" });
      this.player = new VoicePlayer(this.ctx, (turnId) => this.send({ t: "playback", state: "start", turnId, atMs: this.now() }));
    }
    await this.ctx.resume();
    if (this.mic) return;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
    });
    await this.ctx.audioWorklet.addModule("/mic-worklet.js");
    const src = this.ctx.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(this.ctx, "mic-capture");
    node.port.onmessage = (ev) => this.onMicFrame(ev.data as Int16Array);
    src.connect(node);
    this.mic = { stream, node };
    await this.keepAwake();
  }

  private onMicFrame(pcm: Int16Array) {
    if (this.o.onLevel) {
      let s = 0;
      for (let i = 0; i < pcm.length; i += 4) s += pcm[i]! * pcm[i]!;
      this.o.onLevel(Math.sqrt(s / (pcm.length / 4)) / 32768);
    }
    if (!this.live || !this.clockOrigin) return;
    const seq = ++this.seq;
    try {
      localStorage.setItem(this.seqKey(), String(seq));
    } catch {
      /* ignore */
    }
    const captureMs = this.now() - (pcm.length / 16) ; // the frame began ~100 ms ago
    const playback = this.player?.isPlaying() ?? false;
    void this.store.put(seq, captureMs, pcm);
    if (this.ws?.readyState === WebSocket.OPEN && !this.resending) {
      this.ws.send(encodeMicFrame(seq, captureMs, playback ? MIC_FLAG_PLAYBACK : 0, pcm));
    }
  }

  /** After a reconnect: upload everything the server has not confirmed, paced. */
  private async resendUnacked(lastAck: number) {
    // Frames go out strictly in order: the backlog first, then any captured
    // while it uploaded, then live audio resumes.
    this.resending = true;
    let sentUpTo = lastAck;
    try {
      for (;;) {
        const pending = (await this.store.after(sentUpTo)).filter((f) => f.seq <= this.seq);
        if (!pending.length) break;
        for (let i = 0; i < pending.length; i++) {
          const f = pending[i]!;
          if (this.ws?.readyState !== WebSocket.OPEN) return;
          this.ws.send(encodeMicFrame(f.seq, f.captureMs, MIC_FLAG_LATE, new Int16Array(f.pcm)));
          sentUpTo = f.seq;
          if (i % 40 === 39) await new Promise((r) => setTimeout(r, 450));
        }
      }
    } finally {
      this.resending = false;
    }
  }

  private async keepAwake() {
    try {
      const nav = navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<{ release(): Promise<void> }> } };
      this.wakeLock = (await nav.wakeLock?.request("screen")) ?? null;
    } catch {
      /* not supported */
    }
  }

  private async releaseWake() {
    await this.wakeLock?.release().catch(() => undefined);
    this.wakeLock = null;
  }

  close() {
    this.closed = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.ws?.close();
    this.mic?.stream.getTracks().forEach((t) => t.stop());
    this.mic?.node.disconnect();
    void this.ctx?.close();
    void this.releaseWake();
  }
}
